import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { LoadingButton } from "@mui/lab";
import { useMediaQuery, useTheme } from "@mui/material";
import { useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaFileExport, FaSave, FaTrash } from "react-icons/fa";
import { IoMdArrowBack } from "react-icons/io";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { setCompetitorAnalysis } from "../../../../../redux/slices/competitorSlice";
import {
  useDeleteSinglePostEditorBlogMutation,
  useGetLinkTableMutation,
  useUpdateSinglePostEditorBlogMutation,
} from "../../../../../redux/slices/postEditorBlogSlice";
import { dispatch } from "../../../../../redux/store";
import { trackPlatformUsed } from "../../../../../shared/utils/trackers";
import { useProject } from "../projectContext";
import ExportModal from "./exportModal";

function appendIds(htmlString) {
  const div = document.createElement("div");
  div.innerHTML = htmlString;

  const headers = Array.from(div.querySelectorAll("h2, h3, h4, h5, h6"));
  if (headers.length === 0) return htmlString;

  headers.forEach((header) => {
    const text = header.textContent.trim();
    const id = header.id || text.replace(/\s+/g, "-").toLowerCase();
    header.id = id;
  });

  return div.innerHTML;
}

function generateTableOfContents(htmlString) {
  const div = document.createElement("div");
  div.innerHTML = htmlString;

  const headers = Array.from(div.querySelectorAll("h2, h3, h4, h5, h6"));
  if (headers.length === 0) return "";

  let tocHtml = "<ul>";
  let stack = []; // Stack to manage nesting levels
  let lastLevel = 0; // Track last heading level

  headers.forEach((header) => {
    const tagName = header.tagName.toLowerCase();
    const level = parseInt(tagName.replace("h", ""), 10);
    const text = header.textContent.trim();
    const id = header.id || text.replace(/\s+/g, "-").toLowerCase();
    header.id = id;

    // Close lists if we are moving up in hierarchy
    while (stack.length && stack[stack.length - 1] >= level) {
      tocHtml += "</ul></li>";
      stack.pop();
    }

    // Add new heading to TOC
    tocHtml += `<li><a href="#${id}">${text}</a>`;

    // If the next heading is a subheading, open a new nested list
    if (stack.length === 0 || level > stack[stack.length - 1]) {
      tocHtml += "<ul>";
      stack.push(level);
    } else {
      tocHtml += "</li>"; // Close current list item
    }

    lastLevel = level;
  });

  // Close remaining open tags
  while (stack.length) {
    tocHtml += "</ul></li>";
    stack.pop();
  }

  tocHtml += "</ul>";

  tocHtml = tocHtml.replace(/<ul>\s*<\/ul>/g, "");
  tocHtml = tocHtml.replace(/<li>\s*<\/li>/g, "");

  return tocHtml;
}

const PostEditorActions = () => {
  const { id } = useParams();
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const { t } = useTranslation();
  const formikProps = useFormikContext();
  const [open, setOpen] = useState(false);
  const {
    open: exportOpen,
    setOpen: setExportOpen,
    setBlogPost,
  } = useProject();

  const [savePost, { isLoading: isSaving }] =
    useUpdateSinglePostEditorBlogMutation();
  const [deletePostEditor, { isLoading: isDeleting }] =
    useDeleteSinglePostEditorBlogMutation();
  const competitorResults = useSelector(
    (state) => state.competitorAnalysis.data?.data
  );
  const [linkingTable, { isLoading: loadingTable }] = useGetLinkTableMutation();
  const loadLinkingTable = async () => {
    const payload = {
      text:
        formikProps.values.introduction +
        formikProps.values.content +
        formikProps.values.conclusion,
      domain: formikProps.values.url_path,
    };
    const res = await linkingTable(payload).unwrap();
    if (res?.table) {
      dispatch(
        setCompetitorAnalysis({
          data: {
            ...competitorResults,
            linkingTable: res,
          },
        })
      );
    }
  };

  const handleSave = async () => {
    trackPlatformUsed("blog_post", "update_in_db");

    if (formikProps.dirty && formikProps.isValid) {
      try {
        const values = formikProps.values;
        const payload = Object.fromEntries(
          Object.entries(values).map(([key, value]) => [
            key,
            value === null || value === "" ? null : value,
          ])
        );
        console.log(payload);
        const res = await savePost(payload).unwrap();
        if (res?.status === 200) {
          loadLinkingTable();
          toast.success(
            t(
              "changes-have-been-changed",
              "Les changements ont bien été sauvegardés"
            )
          );
          formikProps.resetForm({ values });
        }
      } catch (err) {
        console.log(err);
        toast.error(
          t("error-saving-data", "Une erreur a eu lieu lors de la sauvegarde")
        );
      }
    } else {
      if (!formikProps.isValid) {
        const errorMessages = Object.entries(formikProps.errors)
          .map(([field, message]) => `${message}`)
          .join(", ");

        toast.warning(errorMessages);
      } else {
        toast.warning(
          t(
            "please-complete-fields",
            "Veuillez compléter tous les champs du formulaire"
          )
        );
      }
    }
  };

  const handleDeleteBlogPost = async (id) => {
    try {
      const res = await deletePostEditor({ id }).unwrap();
      //refetch();
      toast.success("Post Deleted Successfully");
      navigate("/blog-posts");
    } catch (error) {
      console.log(error);
      toast.error("Error Occur while deleting");
    }
  };

  const handleExportOpen = async () => {
    const toc = generateTableOfContents(formikProps.values["content"]);
    const contentWithIds = appendIds(formikProps.values["content"]);
    formikProps.setFieldValue("table_of_content", toc);
    const blogPost = {
      url_slug: formikProps.values["url_slug"],
      title: formikProps.values["h1_title"],
      contentWithToc:
        formikProps.values["introduction"] +
        "<p></p>" +
        toc +
        "<p></p>" +
        contentWithIds +
        "<p></p>" +
        formikProps.values["conclusion"],
      content:
        formikProps.values["introduction"] +
        "<p></p>" +
        contentWithIds +
        "<p></p>" +
        formikProps.values["conclusion"],
      meta_title: formikProps.values["meta_title"],
      meta_description: formikProps.values["meta_description"],
      seo_request: formikProps.values["seo_request"],
    };
    setBlogPost(blogPost);
    setExportOpen(true);
  };

  return (
    <div className={`flex gap-1 items-center ${isLarge ? "" : "mr-8"}`}>
      <LoadingButton
        onClick={handleSave}
        size="small"
        disabled={isSaving || !formikProps.dirty}
        loading={isSaving}
        sx={{
          backgroundColor: formikProps.dirty ? "#22c55e" : "#d1d5db",
          textTransform: "none",
          color: formikProps.dirty ? "white" : "#9ca3af",
          ":hover": {
            backgroundColor: "#16a34a",
          },
        }}
        startIcon={<FaSave fill="white" />}
      >
        {/* <FaSave fill='white' /> */}
        <p className="hidden sm:block">{t("Save", "Sauvegarder")}</p>
      </LoadingButton>

      <LoadingButton
        onClick={handleExportOpen}
        size="small"
        sx={{
          backgroundColor: "#5094cf",
          textTransform: "none",
          color: "white",
          ":hover": {
            backgroundColor: "#1a79cb",
          },
        }}
        startIcon={<FaFileExport fill="white" />}
      >
        <p className="hidden sm:block">{t("Export", "Exporter")}</p>
      </LoadingButton>

      <LoadingButton
        size="small"
        onClick={() => setOpen(true)}
        disabled={isDeleting}
        loading={isDeleting}
        sx={{
          backgroundColor: "#ef4444",
          textTransform: "none",
          color: "white",
          ":hover": {
            backgroundColor: "#dc2626",
          },
        }}
        startIcon={<FaTrash fill="white" />}
      >
        <p className="hidden sm:block">{t("Delete", "Supprimer")}</p>
      </LoadingButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        className="relative z-[9999]"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-[999] w-screen overflow-y-auto bg-black/50">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:w-full sm:max-w-2xl sm:p-8 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold leading-6 text-gray-900"
                >
                  <div className="flex items-center gap-3">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-error/30 sm:mx-0 sm:h-10 sm:w-10">
                      <PriorityHighIcon
                        aria-hidden="true"
                        className="h-8 w-8 text-error"
                      />
                    </div>
                    <p className="text-base font-semibold leading-6 text-black/40">
                      {t("delete-blog-post", "Supprimer l'article de blog")}
                    </p>
                  </div>
                </DialogTitle>
                <p className="text-base text-black text-left mt-5">
                  'Êtes-vous sûr de vouloir supprimer votre article de blog ?
                  Les données seront effacées et vous ne pourrez plus les
                  récupérer.'
                </p>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    disabled={isDeleting}
                    onClick={() => handleDeleteBlogPost(id, true)}
                    className="inline-flex w-full justify-center rounded-md bg-error px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                  >
                    Supprimer
                  </button>
                  <button
                    type="button"
                    data-autofocus
                    onClick={() => setOpen(false)}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      <ExportModal open={exportOpen} setOpen={setExportOpen} />
    </div>
  );
};

export const EditorBackButton = () => {
  const navigate = useNavigate();

  const { t } = useTranslation();
  const formikProps = useFormikContext();
  const [open, setOpen] = useState(false);
  const [savePost, { isLoading: isSaving }] =
    useUpdateSinglePostEditorBlogMutation();

  const handleSave = async () => {
    setOpen(false);
    if (formikProps.dirty && formikProps.isValid) {
      try {
        const values = formikProps.values;
        const payload = Object.fromEntries(
          Object.entries(values).map(([key, value]) => [
            key,
            value === null || value === "" ? null : value,
          ])
        );
        console.log(payload);
        const res = await savePost(payload).unwrap();
        if (res?.status === 200) {
          toast.success(
            t(
              "changes-have-been-changed",
              "Les changements ont bien été sauvegardés"
            )
          );
          formikProps.resetForm({ values });
          navigate("/blog-posts");
        }
      } catch (err) {
        console.log(err);
        toast.error(
          t("error-saving-data", "Une erreur a eu lieu lors de la sauvegarde")
        );
      }
    } else {
      if (!formikProps.isValid) {
        const errorMessages = Object.entries(formikProps.errors)
          .map(([field, message]) => `${message}`)
          .join(", ");

        toast.warning(errorMessages);
      } else {
        toast.warning(
          t(
            "please-complete-fields",
            "Veuillez compléter tous les champs du formulaire"
          )
        );
      }
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (formikProps.dirty) {
        event.preventDefault();
        event.returnValue = ""; // Required for browser prompts.
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload); // For tab close/refresh.

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [formikProps.dirty]);

  return (
    <div className="flex items-center">
      <button
        onClick={() => {
          if (formikProps.dirty) {
            setOpen(true);
          } else {
            navigate("/blog-posts");
          }
        }}
      >
        <IoMdArrowBack size={20} />
      </button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        className="relative z-[9999]"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-[999] w-screen overflow-y-auto bg-black/50">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:w-full sm:max-w-2xl sm:p-8 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold leading-6 text-gray-900"
                >
                  <div className="flex items-center gap-3">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary/30 sm:mx-0 sm:h-10 sm:w-10">
                      <PriorityHighIcon
                        aria-hidden="true"
                        className="h-8 w-8 text-primary"
                      />
                    </div>
                    <p className="text-base font-semibold leading-6 text-black/40">
                      {t(
                        "save-changes-security",
                        "Sauvegarder les changements ?"
                      )}
                    </p>
                  </div>
                </DialogTitle>
                <p className="text-base text-black text-left mt-5">
                  {t(
                    "save-changes-before-quit-text",
                    "Certains changements que vous avez effectué n'ont pas été sauvegardés. Voulez-vous les enregistrer avant de quitter ?"
                  )}
                </p>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleSave}
                    className="inline-flex w-full justify-center rounded-md bg-green-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 sm:ml-3 sm:w-auto"
                  >
                    Oui, sauvegarder
                  </button>
                  <button
                    type="button"
                    data-autofocus
                    onClick={() => navigate("/blog-posts")}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-red-500 px-3 py-2 text-sm font-semibold text-white shadow-sm  hover:bg-red-700 sm:mt-0 sm:w-auto"
                  >
                    Quitter sans sauvegarder
                  </button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default PostEditorActions;
